import { useEffect, useState, type FormEvent } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_ADDR = 'https://readdy.ai/api/form/d9oq2iiqs38sj6jc7dgg';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    document.title = '关于我们 - 51cg吃瓜网 | 品牌介绍与联系方式';
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('website_alt');

    try {
      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = (parsed && (parsed as Record<string, unknown>).meta && (parsed as Record<string, unknown>).meta && (parsed as Record<string, Record<string, unknown>>).meta.message) as string
          || (parsed && (parsed as Record<string, string>).message)
          || '提交失败，请稍后重试';
        setFormStatus('error');
        setFormError(msg);
      }
    } catch {
      setFormStatus('error');
      setFormError('网络错误，请检查网络后重试');
    }
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: aboutData.brandName,
    url: 'https://51cg.fun',
    description: aboutData.description,
    slogan: aboutData.slogan,
    contactPoint: {
      '@type': 'ContactPoint',
      email: aboutData.contactInfo.email,
      contactType: 'customer service',
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: aboutData.contactInfo.address,
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '51cg吃瓜网是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51cg吃瓜网（51cg.fun）是国内领先的娱乐八卦与热点资讯聚合平台，提供黑料爆料、莫里秀全集、找爹视频、情感八卦等丰富内容。',
        },
      },
      {
        '@type': 'Question',
        name: '51cg莫里秀在哪里可以观看？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51cg莫里秀全集可在51cg吃瓜网莫里秀专题板块在线观看。我们持续更新莫里秀最新节目内容，包括找爹经典名场面和感人认亲故事。',
        },
      },
      {
        '@type': 'Question',
        name: '如何向51cg投稿爆料？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过51cg吃瓜网的联系我们页面提交爆料信息，或发送邮件至 contact@51cg.fun。我们的编辑团队会认真审核每一条投稿。',
        },
      },
      {
        '@type': 'Question',
        name: '51cg吃瓜网的内容更新频率如何？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51cg吃瓜网保持每日更新的频率，确保用户能够第一时间获取最新最热的吃瓜资讯。热点事件发生后，我们会在最短时间内发布相关报道。',
        },
      },
      {
        '@type': 'Question',
        name: '51cg.fun黑料吃瓜网的内容真实可靠吗？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '51cg.fun黑料吃瓜网拥有专业的内容审核团队，所有发布的资讯均经过严格核实。我们致力于为用户提供真实可靠的吃瓜内容。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://51cg.fun' },
      { '@type': 'ListItem', position: 2, name: '关于我们' },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <Navbar />
      <main className="pt-20 pb-10">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          <section className="py-8 md:py-12">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <h1 className="text-3xl md:text-4xl font-bold font-heading text-foreground-950 mb-4">
                关于{aboutData.brandName}
              </h1>
              <p className="text-lg text-primary-500 font-medium mb-4 font-heading">
                {aboutData.slogan}
              </p>
              <p className="text-foreground-600 leading-relaxed">
                {aboutData.description}
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-16">
              {aboutData.stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center p-6 rounded-xl bg-background-100"
                >
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-500">
                    <i className={stat.icon}></i>
                  </div>
                  <div className="text-2xl font-bold text-foreground-950 font-label mb-1">
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-600">{stat.label}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  我们的使命
                </h2>
                <p className="text-foreground-600 leading-relaxed">
                  {aboutData.mission}
                </p>
              </div>
              <div>
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-4">
                  我们的愿景
                </h2>
                <p className="text-foreground-600 leading-relaxed">
                  {aboutData.vision}
                </p>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
                发展历程
              </h2>
              <div className="relative">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-200 hidden lg:block"></div>
                <div className="space-y-8">
                  {aboutData.history.map((item, index) => (
                    <div
                      key={item.year}
                      className={`flex flex-col lg:flex-row items-center gap-6 ${
                        index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                      }`}
                    >
                      <div className={`flex-1 ${index % 2 === 0 ? 'text-center lg:text-right' : 'text-center lg:text-left'}`}>
                        <div className="bg-background-100 rounded-xl p-5">
                          <h3 className="text-lg font-bold font-heading text-foreground-950 mb-1">
                            {item.title}
                          </h3>
                          <p className="text-sm text-foreground-600">{item.content}</p>
                        </div>
                      </div>
                      <div className="flex-shrink-0 flex items-center justify-center">
                        <div className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold font-label text-sm">
                          {item.year}
                        </div>
                      </div>
                      <div className="flex-1 hidden lg:block"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-8 text-center">
                用户口碑
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {aboutData.testimonials.map((testimonial) => (
                  <div
                    key={testimonial.name}
                    className="bg-background-100 rounded-xl p-6"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                        <img
                          src={testimonial.avatar}
                          alt={testimonial.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground-900">
                          {testimonial.name}
                        </div>
                        <div className="text-xs text-foreground-500">
                          {testimonial.role}
                        </div>
                      </div>
                    </div>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      &ldquo;{testimonial.content}&rdquo;
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div id="contact" className="bg-background-100 rounded-2xl p-8 md:p-10">
              <h2 className="text-2xl font-bold font-heading text-foreground-950 mb-6 text-center">
                联系我们
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">电子邮箱</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">微信公众号</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                      <i className="ri-weibo-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">官方微博</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.weibo}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-950">办公地址</h3>
                      <p className="text-sm text-foreground-600">{aboutData.contactInfo.address}</p>
                    </div>
                  </div>
                </div>

                <div>
                  <form data-readdy-form="true" onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <input
                        type="text"
                        name="name"
                        placeholder="您的姓名"
                        className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <input
                        type="email"
                        name="email"
                        placeholder="您的邮箱"
                        className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
                      />
                    </div>
                    <div>
                      <textarea
                        name="message"
                        placeholder="请输入您的留言..."
                        rows={4}
                        maxLength={500}
                        className="w-full px-4 py-3 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                      ></textarea>
                    </div>
                    <div className="website-alt-field">
                      <input
                        type="text"
                        name="website_alt"
                        tabIndex={-1}
                        autoComplete="off"
                        aria-hidden="true"
                        readOnly
                      />
                    </div>
                    {formStatus === 'success' && (
                      <div className="text-sm text-accent-500 bg-accent-50 rounded-lg px-4 py-3">
                        消息已发送成功！我们会尽快回复您。
                      </div>
                    )}
                    {formStatus === 'error' && (
                      <div className="text-sm text-primary-500 bg-primary-50 rounded-lg px-4 py-3">
                        {formError}
                      </div>
                    )}
                    <button
                      type="submit"
                      className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      发送消息
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
}